import type { MouseEvent } from 'react';

import {
  SIDEBAR_PANEL_NAV_HOVER_ATTR,
  SIDEBAR_PANEL_NAV_HOVER_VALUE,
} from '@/app-shell/layout/Navigation/sidebar-panel/sidebar-overlay-constants';

export const routePath = (href: string): string => href.replace(/[?#].*$/, '');

/**
 * A path segment that looks like an identifier rather than a route word: at
 * least 8 chars of `[A-Za-z0-9_-]` containing a digit. Matches Mongo ObjectIds,
 * UUIDs and short-id slugs; never matches route words like `my-templates`,
 * `coding-dashboard` or `dictation-logs` (no digit).
 */
const ID_SEGMENT = /^(?=.*\d)[A-Za-z0-9_-]{8,}$/;

/**
 * Low-cardinality label for a nav destination, safe to send to telemetry.
 * Strips query/hash like {@link routePath}, collapses the two session routes
 * to their bare path (the Scribe row's href is `/scribe/sessions/<id>` in
 * new-session-list mode), and then drops ANY remaining id-shaped segment. The
 * last rule is the fail-safe one: a future sidebar row pointing at
 * `/patients/<id>` or `/scribe/chat/<chatId>` must not leak the id to Datadog
 * (or to the abandonment reporter's `localStorage` record) just because nobody
 * remembered to add its route here.
 */
export const toRouteLabel = (href: string): string =>
  routePath(href)
    .replace(/^(\/scribe\/sessions?)\/.+$/, '$1')
    .split('/')
    .filter((segment, i) => i === 0 || !ID_SEGMENT.test(segment))
    .join('/');

export const routeMatches = (
  currentHref: string,
  targetHref: string,
  match: 'prefix' | 'exact'
) =>
  match === 'exact' || targetHref === '/'
    ? currentHref === targetHref
    : currentHref === targetHref || currentHref.startsWith(`${targetHref}/`);

export const isSameTabNavigation = (event: MouseEvent<HTMLAnchorElement>) =>
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;

/**
 * The frame-sampler label for a nav row (ARD-1977): `nav_` plus the row's
 * route label, which is the same id-stripped, low-cardinality value the nav
 * menu switch flow already sends as `nav_item`. It is derived from `href`
 * and never from the test id: test ids are caller-authored strings with no
 * guarantee against runtime data, whereas {@link toRouteLabel} drops any
 * id-shaped segment by construction. A row with no route has no marker and
 * reports under its tag.
 */
export const navFrameSurface = (href?: string): string | undefined => {
  if (!href) return undefined;
  const slug = toRouteLabel(href)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return slug ? `nav_${slug}` : 'nav';
};

/**
 * Marker attributes on a nav row's `SidebarMenuItem`: the sidebar-panel hover
 * bridge (only when the row opens a panel) and the row's name for the global
 * frame sampler, which labels a click episode from the nearest
 * `data-frame-surface` ({@link navFrameSurface}).
 */
export const menuItemMarkerAttributes = ({
  shouldShowSidebarPanel,
  href,
}: {
  shouldShowSidebarPanel: boolean;
  href?: string;
}): Record<string, string | undefined> => ({
  ...(shouldShowSidebarPanel
    ? { [SIDEBAR_PANEL_NAV_HOVER_ATTR]: SIDEBAR_PANEL_NAV_HOVER_VALUE }
    : {}),
  'data-frame-surface': navFrameSurface(href),
});
